import { Link } from "react-router-dom";
import { formatTicketCode } from "../lib/ticketCode";

/**
 * Header pills showing every active ticket held by the visitor (max 3).
 * Each pill is rendered as a tilted outlined ticket shape (à la spec
 * 2026-02 fork 17 ref image): bleu marine outline, fond transparent,
 * incliné de ~-10° pour donner du dynamisme. Le code du ticket est
 * centré "à l'intérieur" du dessin, lui aussi incliné pour rester
 * solidaire de la forme. Clic → /my-ticket?focus=<id>.
 */
export default function ActiveTicketsPills({ tickets }) {
  if (!tickets || tickets.length === 0) return null;

  return (
    <div
      className="flex items-center gap-1.5 sm:gap-2"
      data-testid="nav-active-tickets-pills"
    >
      {tickets.map((t) => {
        const isCalled = t.status === "called";
        return (
          <Link
            key={t.id}
            to={`/my-ticket?focus=${encodeURIComponent(t.id)}`}
            className="relative inline-flex items-center justify-center w-[64px] h-11 sm:w-[72px] sm:h-12 text-brand hover:text-brand-hover transition-colors"
            data-testid={`nav-active-ticket-pill-${t.id}`}
            aria-label={`Voir le ticket ${formatTicketCode(t)}${
              t.commerceName ? " — " + t.commerceName : ""
            }`}
            title={t.commerceName || ""}
          >
            {/* Tilted, outlined ticket shape with code inside.
                Both shape AND code share the rotation so the number stays
                visually "glued" to the ticket — feels like a real torn
                stub rather than text on an icon. */}
            <span
              className="absolute inset-0 flex items-center justify-center"
              style={{ transform: "rotate(-10deg)" }}
            >
              <svg
                viewBox="0 0 72 36"
                className="absolute inset-0 w-full h-full"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinejoin="round"
                strokeLinecap="round"
                aria-hidden="true"
              >
                {/* Outlined ticket stub with two pronounced semicircular
                    indents on the LEFT and RIGHT edges (the universal
                    "tear here" perforation). Rounded outer corners. */}
                <path
                  d="M 6 4
                     L 66 4
                     A 4 4 0 0 1 70 8
                     L 70 14
                     A 4 4 0 0 0 70 22
                     L 70 28
                     A 4 4 0 0 1 66 32
                     L 6 32
                     A 4 4 0 0 1 2 28
                     L 2 22
                     A 4 4 0 0 0 2 14
                     L 2 8
                     A 4 4 0 0 1 6 4 Z"
                />
                {/* Center vertical dashed perforation */}
                <line
                  x1="36"
                  y1="6"
                  x2="36"
                  y2="30"
                  strokeDasharray="2 2.4"
                  strokeWidth="1.4"
                />
              </svg>
              <span
                className="relative font-mono font-black text-[11px] sm:text-[12px] leading-none tracking-tight z-10"
              >
                {formatTicketCode(t)}
              </span>
            </span>
            {isCalled && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3 z-20">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#34C759] opacity-75 animate-ping" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-[#34C759]" />
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}
